import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { z } from "zod";
import { track } from "@/lib/analytics";

const emailSchema = z
  .string()
  .trim()
  .min(1, "Please enter your email.")
  .max(254, "That email is too long.")
  .email("That doesn't look like a valid email.");

interface WaitlistFormProps {
  /** Which CTA / surface this submission came from. */
  source?: string;
}

type State =
  | { kind: "idle" }
  | { kind: "submitting" }
  | { kind: "success"; already: boolean }
  | { kind: "error"; message: string };

export const WaitlistForm = ({ source = "waitlist-section" }: WaitlistFormProps) => {
  const [email, setEmail] = useState("");
  const [state, setState] = useState<State>({ kind: "idle" });

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (state.kind === "submitting") return;

    const parsed = emailSchema.safeParse(email);
    if (!parsed.success) {
      setState({ kind: "error", message: parsed.error.issues[0].message });
      return;
    }

    setState({ kind: "submitting" });
    const { error } = await supabase
      .from("waitlist")
      .insert({ email: parsed.data.toLowerCase(), source });

    if (!error) {
      track("waitlist_submitted", { source });
      setState({ kind: "success", already: false });
      setEmail("");
      return;
    }

    // 23505 = unique_violation → already on the list (treat as success).
    if ((error as { code?: string }).code === "23505") {
      track("waitlist_submitted", { source, already: true });
      setState({ kind: "success", already: true });
      setEmail("");
      return;
    }

    setState({
      kind: "error",
      message: "Something went quiet on our end. Please try again in a moment.",
    });
  };

  if (state.kind === "success") {
    return (
      <div className="rounded-2xl border border-border bg-card/80 p-6">
        <p className="font-display text-lg text-foreground">
          {state.already
            ? "You're already on the list ✓"
            : "You're on the list ✓"}
        </p>
        <p className="mt-2 text-sm text-ink-soft">
          We'll write when there's something worth your quiet attention —
          not before.
        </p>
      </div>
    );
  }

  const isSubmitting = state.kind === "submitting";

  return (
    <form onSubmit={onSubmit} noValidate className="space-y-3">
      <div className="flex w-full flex-col gap-2 sm:flex-row">
        <label htmlFor="waitlist-email" className="sr-only">
          Email address
        </label>
        <input
          id="waitlist-email"
          type="email"
          inputMode="email"
          autoComplete="email"
          required
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            if (state.kind === "error") setState({ kind: "idle" });
          }}
          placeholder="you@email.com"
          maxLength={254}
          disabled={isSubmitting}
          className="h-12 flex-1 rounded-full border border-border bg-card px-5 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30 disabled:opacity-60"
        />
        <button
          type="submit"
          disabled={isSubmitting}
          className="inline-flex h-12 items-center justify-center rounded-full bg-foreground px-6 text-sm font-medium text-background transition-opacity hover:opacity-90 disabled:opacity-60"
        >
          {isSubmitting ? "Joining…" : "Join the waitlist"}
        </button>
      </div>

      {state.kind === "error" && (
        <p className="text-xs text-destructive" role="alert">
          {state.message}
        </p>
      )}

      <p className="text-xs text-muted-foreground">
        One email when we're ready. No spam, no urgency, easy to leave.
      </p>
    </form>
  );
};

export default WaitlistForm;
